import React, { useState, useRef, useEffect } from "react"
import { Card, CardContent, Paper, Typography, makeStyles, useTheme, Switch, IconButton } from "@material-ui/core"
import { Chart } from "react-google-charts"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
import { Skeleton } from "@material-ui/lab"
import moment from "moment"
import { roles } from "../../../data/roles"
import { ArrowBackIosOutlined as ArrowBack, ArrowForwardIosOutlined as ArrowForward } from "@material-ui/icons"
import { setDashboardReview } from "../../../actions/dashboardActions"

const useStyles = makeStyles((theme) => ({
	root: {
		//minHeight: "438px",
	},
	cardBody: {
		padding: 0,
		"&:last-child": {
			paddingBottom: 0,
		},
	},
	title: {
		color: theme.palette.grey.D100,
		fontWeight: 400,
		marginLeft: "20px",
		padding: "24px 0 10px 0",
	},
	switches: {
		color: theme.palette.text.primary,
	},
	skeletonWrapper: {
		padding: 15,
	},
	NoData: {
		minHeight: 200,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginTop: "4px",
	},
	resetIcon: {
		color: theme.palette.grey.D100,
		cursor: "pointer",
		marginRight: "20px",
	},
	switchesMainDiv: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-around",
		padding: "6px 28px 0 28px",
	},
	switchAChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.primary.dark,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.primary.dark,
		},
	},
	switchBChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.dashboard.D100,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.dashboard.D100,
		},
	},
	switchCChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.dashboard.D110,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.dashboard.D110,
		},
	},
	tooltipDiv: {
		display: "flex",
		alignItems: "center",
	},
	tooltipLabel: {
		...theme.typography.caption,
		color: theme.palette.grey.D100,
		whiteSpace: "nowrap",
		paddingLeft: 2,
	},
	tooltipValue: {
		...theme.typography.caption,
		color: theme.palette.text.primary,
		marginLeft: 3,
		fontWeight: theme.typography.fontWeightMedium,
		whiteSpace: "nowrap",
	},
	tooltipTopDiv: {
		display: "flex",
		alignItems: "center",
		padding: "2px 0",
		gap: "5px",
	},
	tooltipCircleIcon: {
		height: 8,
		width: 8,
		borderRadius: 0,
		marginLeft: 5,
	},
	pagination: {
		display: "flex",
		alignItems: "center",
		justifyContent: "end",
		gap: 5,
		padding: "5px 10px",
		"& svg": {
			cursor: "pointer",
		},
	},
	tooltipTitle: {
		fontSize: 12,
		fontWeight: 500,
		textWrap: "nowrap",
	},
	tooltipSubTitle: {
		fontSize: 12,
		fontWeight: 400,
		color: theme.palette.grey.D100,
	},
	tooltipWrapper: {
		padding: "8px 6px",
		borderRadius: 3,
	},
	dueDate: {
		color: theme.palette.error.main,
	},
}))

const ReviewOverview = ({ getUserReviewDetails }) => {
	const isCaseUser = useSelector((state) => state?.authtentication?.user?.user?.roles)?.includes(roles?.Case_User)
	const isCaseAdmin = useSelector((state) => state?.authtentication?.user?.user?.roles)?.includes(roles?.Case_Administrator)
	const reviewList = useSelector((state) => state.review?.allReviewlist)

	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	const isSenderSelected = useRef(null)
	const [t] = useTranslation("global")

	const colors =
		isCaseUser && !isCaseAdmin
			? [theme.palette.dashboard.D110, theme.palette.dashboard.D100]
			: [theme.palette.dashboard.D110, theme.palette.dashboard.D100, theme.palette.dashboard.D120]
	const types =
		isCaseUser && !isCaseAdmin
			? { ASSIGNED: "Assigned files", REVIEWED: "Reviewed files" }
			: { UNASSIGNED: "Unassigned files", REVIEWED: "Reviewed files", TOTAL: "Total files" }
	const typesColor =
		isCaseUser && !isCaseAdmin
			? { ASSIGNED: theme.palette.dashboard.D110, REVIEWED: theme.palette.dashboard.D100 }
			: { UNASSIGNED: theme.palette.dashboard.D120, REVIEWED: theme.palette.dashboard.D100, TOTAL: theme.palette.dashboard.D110 }

	const mapDomToTooltip = (dueDate, data, type) => {
		switch (type) {
			case types.UNASSIGNED:
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.UNASSIGNED}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("dashboard.unassignedFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("review.reviewForm.dueDate")}:</span>
								<span class="${classes.tooltipValue}">${dueDate ? moment(dueDate, "DD/MM/YYYY").format("DD MMM YYYY") : "-"} </span>
							</div>
						</div>
					</div>`
			case types.ASSIGNED:
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.ASSIGNED}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("dashboard.assignedFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("review.reviewForm.dueDate")}:</span>
								<span class="${classes.tooltipValue}">${dueDate ? moment(dueDate, "DD/MM/YYYY").format("DD MMM YYYY") : "-"} </span>
							</div>
						</div>
					</div>`
			case types.REVIEWED:
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.REVIEWED}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("dashboard.reviewedFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("review.reviewForm.dueDate")}:</span>
								<span class="${classes.tooltipValue}">${dueDate ? moment(dueDate, "DD/MM/YYYY").format("DD MMM YYYY") : "-"} </span>
							</div>
						</div>
					</div>`
			case types.TOTAL: {
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.TOTAL}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("tableColumns.totalFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("review.reviewForm.dueDate")}:</span>
								<span class="${classes.tooltipValue}">${dueDate ? moment(dueDate, "DD/MM/YYYY").format("DD MMM YYYY") : "-"} </span>
							</div>
						</div>
					</div>`
			}
			default:
				return ""
		}
	}

	const mapDataToTooltip = (dueDate, data) => {
		return isCaseUser && !isCaseAdmin
			? `<div class="${classes.tooltipWrapper}">
			<div class="${classes.tooltipTitle}">${data[0]}</div>
			<div class="${classes.tooltipDiv}">
				<span class="${classes.tooltipLabel}">${t("review.reviewForm.dueDate")}:</span>
				<span class="${classes.tooltipValue} ${classes.dueDate}">${dueDate ? moment(dueDate, "DD/MM/YYYY").format("DD/MM/YYYY") : "-"} </span>
			</div>
			<div class="${classes.tooltipSubTitle}">${t("dashboard.files")}</div>

			<div class="${classes.tooltipTopDiv}" style="padding-top:0">
				<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.ASSIGNED}"></div>
				<div class="${classes.tooltipDiv}">
					<span class="${classes.tooltipLabel}">${t("dashboard.assignedFiles")}:</span>
					<span class="${classes.tooltipValue}">${data[2]}</span>
				</div>
			</div>

			<div class="${classes.tooltipTopDiv}">
				<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.REVIEWED}"></div>
				<div class="${classes.tooltipDiv}">
					<span class="${classes.tooltipLabel}">${t("dashboard.reviewedFiles")}:</span>
					<span class="${classes.tooltipValue}">${data[1]}</span>
				</div>
			</div>
		</div>`
			: `<div class="${classes.tooltipWrapper}">
			<div class="${classes.tooltipTitle}">${data[0]}</div>
			<div class="${classes.tooltipSubTitle}">${t("dashboard.files")}</div>

			<div class="${classes.tooltipTopDiv}" style="padding-top:0">
				<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.TOTAL}"></div>
				<div class="${classes.tooltipDiv}">
					<span class="${classes.tooltipLabel}">${t("dashboard.totalFiles")}:</span>
					<span class="${classes.tooltipValue}">${data[3]}</span>
				</div>
			</div>

			<div class="${classes.tooltipTopDiv}">
				<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.REVIEWED}"></div>
				<div class="${classes.tooltipDiv}">
					<span class="${classes.tooltipLabel}">${t("dashboard.reviewedFiles")}:</span>
					<span class="${classes.tooltipValue}">${data[2]}</span>
				</div>
			</div>

			<div class="${classes.tooltipTopDiv}">
				<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.UNASSIGNED}"></div>
				<div class="${classes.tooltipDiv}">
					<span class="${classes.tooltipLabel}">${t("dashboard.unassignedFiles")}:</span>
					<span class="${classes.tooltipValue}">${data[1]}</span>
				</div>
			</div>
		</div>`
	}

	const addCustomToolTip = (chartTempData) => {
		const initializeArray = [chartTempData?.[0]?.[0]]
		if (chartTempData?.[0]?.[3]) {
			initializeArray.push(...[chartTempData[0][3], { role: "tooltip", p: { html: true } }])
		}
		if (chartTempData?.[0]?.[2]) {
			initializeArray.push(...[chartTempData[0][2], { role: "tooltip", p: { html: true } }])
		}
		if (chartTempData?.[0]?.[1]) {
			initializeArray.push(...[chartTempData[0][1], { role: "tooltip", p: { html: true } }])
		}
		return chartTempData.reduce(
			(result, currItem, index) => {
				const currArray = [currItem[0]]
				if (index !== 0) {
					if (currItem?.[3] >= 0) {
						currArray.push(...[currItem[3], mapDataToTooltip(dateData[currItem[0]]?.due_date, currItem)])
					}
					if (currItem?.[2] >= 0) {
						currArray.push(...[currItem[2], mapDataToTooltip(dateData[currItem[0]]?.due_date, currItem)])
					}
					if (currItem?.[1] >= 0) {
						currArray.push(...[currItem[1], mapDataToTooltip(dateData[currItem[0]]?.due_date, currItem)])
					}
					result.push(currArray)
				}
				return result
			},
			[[...initializeArray]]
		)
	}

	const prepareData = (data, firstIndex, lastIndex) => {
		const cloneData = data?.length ? _.clone(data) : []
		const headers = cloneData[0]

		const newData = cloneData.slice(firstIndex, lastIndex)
		if (firstIndex !== 0) {
			newData.unshift(headers)
		}

		const selectedReview = reviewList?.find((item) => item?.name === newData?.[1]?.[0])
		if (selectedReview?.id && newData?.length > 1) {
			getUserReviewDetails(selectedReview?.id)
			dispatch(setDashboardReview(selectedReview))
		}

		return newData
	}

	const data = useSelector((state) => state.info?.reviewOverviewDetails?.review_overview || [])
	const dateData = useSelector((state) => state.info?.reviewOverviewDetails?.review_overview_due_date || {})
	const isLoading = useSelector((state) => state.info?.isReviewOverviewLoading)
	const [graphColors, setGraphColors] = useState([...colors])
	const [chartData, setChartData] = useState([])
	const [currentPage, setCurrentPage] = useState(1)
	const itemPerPage = 9
	const indexOfLastItem = currentPage * itemPerPage
	const indexOfFirstitem = indexOfLastItem - itemPerPage
	const [currentItems, setCurrentItems] = useState([])

	const handleReviewSelect = (review) => {
		const selectedReview = reviewList?.find((item) => item?.name === review[0])
		if (selectedReview?.id) {
			getUserReviewDetails(selectedReview?.id)
			dispatch(setDashboardReview(selectedReview))
		}
	}

	const nextPage = () => {
		if (currentPage < Math.ceil(chartData.length / itemPerPage)) {
			setCurrentPage((prev) => currentPage + 1)
		}
	}

	const prevPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1)
		}
	}

	useEffect(() => {
		if (data?.length > 0) {
			setChartData((prev) => addCustomToolTip(data))
		}
	}, [data])

	useEffect(() => {
		if (chartData?.length > 1) {
			const items = prepareData(chartData, indexOfFirstitem, indexOfLastItem)
			setCurrentItems((prev) => items)
		}
	}, [chartData, currentPage, reviewList])

	//const currentItems = prepareData(chartData, indexOfFirstitem, indexOfLastItem)

	const [isChecked, setIsChecked] = useState({ isTotalFilesChecked: true, isReviewedFilesChecked: true, isUnassignedFilesChecked: true })

	const options = {
		tooltip: {
			isHtml: true,
		},
		title: "",
		height: isCaseAdmin ? 400 : 400,
		legend: {
			position: "bottom",
			alignment: "end",
			labels: {
				boxWidth: 10,
				usePointStyle: true,
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animation: {
			startup: true,
			duration: 1000,
			easing: "inAndOut",
		},
		colors: graphColors,
		chartArea: {
			left: 40,
			right: 40,
			top: 20,
			bottom: isCaseAdmin ? 93 : 93,
			width: "100%",
		},
		//isStacked: true,
		vAxis: {
			baselineColor: "red",
			gridlines: {
				color: "none",
				zeroLineColor: "transparent",
			},
			baselineColor: theme.palette.grey[300],
		},
		hAxis: { textPosition: "out" },
		bar: { groupWidth: 40 },
		seriesType: "bars",
		fontSize: 12,
	}

	const handleGraphColorChange = (currentStates) => {
		const graphColors = []
		if (currentStates?.isUnassignedFilesChecked) {
			graphColors.push(theme.palette.dashboard.D110)
		}
		if (currentStates?.isReviewedFilesChecked) {
			graphColors.push(theme.palette.dashboard.D100)
		}
		if (currentStates?.isTotalFilesChecked) {
			graphColors.push(theme.palette.primary.dark)
		}
		setGraphColors(graphColors)
	}

	const handleChange = (event) => {
		const currentStates = { ...isChecked, [event.target.name]: event.target.checked }
		handleGraphColorChange(currentStates)
		setChartData((prev) =>
			addCustomToolTip(
				_.cloneDeep(data).map((item) => {
					const filteredDataItem = [item?.[0]]
					if (currentStates?.isUnassignedFilesChecked) {
						filteredDataItem?.push(item?.[1])
					}
					if (currentStates?.isReviewedFilesChecked) {
						filteredDataItem?.push(item?.[2])
					}
					if (currentStates?.isTotalFilesChecked) {
						filteredDataItem?.push(item?.[3])
					}
					return filteredDataItem
				})
			)
		)
		setIsChecked((prevData) => ({ ...prevData, [event.target.name]: event.target.checked }))
	}

	return (
		<Card className={classes.root} elevation="0">
			<CardContent className={classes.cardBody}>
				{isLoading ? (
					<div className={classes.skeletonWrapper}>
						<Skeleton variant="rect" width={"100%"} height={300} />
					</div>
				) : chartData?.length > 1 ? (
					<>
						<Chart
							chartType="ComboChart"
							data={currentItems}
							options={options}
							key={currentPage} //key is added: if currentItems changes then callback will reinitialized
							chartEvents={[
								{
									eventName: "ready",
									callback: ({ chartWrapper, google }) => {
										const chart = chartWrapper.getChart()
										google.visualization.events.addListener(chart, "click", (e) => {
											if (isCaseAdmin) {
												var selection = chart.getSelection()
												const index = parseInt(e?.targetID?.split("#")?.[2])
												if (index || index === 0) {
													handleReviewSelect(currentItems?.[index + 1])
												}
											}
										})
									},
								},
							]}
						/>

						<div className={classes.pagination} style={{ visibility: chartData?.length > itemPerPage ? "" : "hidden" }}>
							<IconButton onClick={prevPage} color="primary" aria-label="prev" disabled={currentPage === 1} title="Previous">
								<ArrowBack />
							</IconButton>
							<IconButton
								onClick={nextPage}
								color="primary"
								aria-label="next"
								disabled={currentPage === Math.ceil(chartData?.length / itemPerPage)}
								title="Next"
							>
								<ArrowForward />
							</IconButton>
						</div>
					</>
				) : (
					<Typography variant="subtitle1" className={classes.NoData} style={{ minHeight: isCaseUser && !isCaseAdmin ? 480 : "" }}>
						{t("dashboard.noDataFound")}
					</Typography>
				)}
			</CardContent>
		</Card>
	)
}

export default React.memo(ReviewOverview)
